import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import councilPortalService from "api/councilPortalService";
import councilService from "api/councilService";
import { ICouncilAgendaItem } from "dto/Council/ICouncil";
import {
  DESCRIBED_PROTOCOL_TYPES,
  ECouncilProtocolItemType,
  ICouncilProtocolItem,
  MANUAL_PROTOCOL_TYPES,
  PERSONAL_PROTOCOL_TYPES,
  protocolTypeLabel,
} from "dto/Council/ICouncilPortal";
import { ENotificationType, notification } from "utils/notification";

import { decodeText } from "./decodeText";
import styles from "./styles.module.scss";

// Biuro rady → „Przebieg sesji". Dziennik zdarzeń, z którego składany jest protokół.
//
// Odwzorowanie ProtocolView z Magistratu: część wpisów pojawia się sama z przebiegu
// obrad (otwarcie sesji, przejście do punktu, obecność, głosowania, przerwa), a
// protokolant dopisuje to, czego z samego przebiegu odczytać nie sposób — wystąpienia,
// wnioski, sprzeciwy, interpelacje i zapytania.
//
// Wpisy samoczynne są tu tylko do wglądu: ich edycja albo usunięcie oznaczałaby
// protokół rozjeżdżający się z tym, co faktycznie zaszło na sali.

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });

interface Props {
  sessionId: string;
  /** Punkt, do którego domyślnie przypisujemy nowy wpis. */
  activeAgendaItemId: string | null;
  agendaItems: ICouncilAgendaItem[];
  /** Sesja zamknięta — dziennika już się nie uzupełnia. */
  readOnly: boolean;
  /** Osoba wskazana z góry, np. radny, któremu właśnie udzielono głosu. */
  defaultCouncilorId?: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

const ProtocolJournalModal = ({
  sessionId,
  activeAgendaItemId,
  agendaItems,
  readOnly,
  defaultCouncilorId,
  onClose,
  onChanged,
}: Props) => {
  const itemsQuery = councilPortalService.useGetProtocolItems(sessionId);
  const councilorsQuery = councilPortalService.useGetCouncilors();
  const guestsQuery = councilService.useGetGuests(sessionId);

  const { mutate: save, isLoading: saving } = councilPortalService.useSaveProtocolItem();
  const { mutate: remove } = councilPortalService.useRemoveProtocolItem();

  const [editing, setEditing] = useState<ICouncilProtocolItem | null>(null);
  const [adding, setAdding] = useState(!!defaultCouncilorId);

  const [type, setType] = useState<ECouncilProtocolItemType>(
    ECouncilProtocolItemType.PersonTookVote,
  );
  const [personId, setPersonId] = useState<string>(defaultCouncilorId ?? "");
  const [agendaItemId, setAgendaItemId] = useState<string>(activeAgendaItemId ?? "");
  const [description, setDescription] = useState("");

  const items = itemsQuery.data?.data ?? [];
  const councilors = councilorsQuery.data?.data ?? [];

  const guests = useMemo(() => guestsQuery.data?.data ?? [], [guestsQuery.data]);
  const guestIds = useMemo(() => new Set(guests.map((g) => g.Id)), [guests]);

  const needsPerson = PERSONAL_PROTOCOL_TYPES.includes(type);
  const needsDescription = DESCRIBED_PROTOCOL_TYPES.includes(type);

  const canApply =
    (!needsPerson || !!personId) && (!needsDescription || !!description.trim());

  const resetForm = () => {
    setEditing(null);
    setAdding(false);
    setType(ECouncilProtocolItemType.PersonTookVote);
    setPersonId("");
    setAgendaItemId(activeAgendaItemId ?? "");
    setDescription("");
  };

  const startEdit = (item: ICouncilProtocolItem) => {
    setEditing(item);
    setAdding(true);
    setType(item.Type);
    setPersonId(item.CouncilorId ?? item.GuestId ?? "");
    setAgendaItemId(item.AgendaItemId ?? "");
    setDescription(decodeText(item.Description));
  };

  const fail = (title: string) => (e: unknown) =>
    notification({
      type: ENotificationType.ERROR,
      title,
      text:
        (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
        "Operacja się nie powiodła.",
    });

  const submit = () => {
    if (!canApply) return;

    // Ta sama lista wyboru obsługuje radnych i gości, więc rozstrzygamy po zbiorze
    // identyfikatorów, a nie po osobnych polach formularza.
    const isGuest = !!personId && guestIds.has(personId);

    save(
      {
        sessionId,
        request: {
          Id: editing?.Id ?? null,
          AgendaItemId: agendaItemId || null,
          Type: type,
          CouncilorId: needsPerson && !isGuest ? personId || null : null,
          GuestId: needsPerson && isGuest ? personId : null,
          Description: description.trim() || null,
        },
      },
      {
        onSuccess: () => {
          itemsQuery.refetch();
          onChanged?.();
          resetForm();
        },
        onError: fail("Nie udało się zapisać wpisu"),
      },
    );
  };

  const doRemove = (item: ICouncilProtocolItem) =>
    remove(item.Id, {
      onSuccess: () => {
        itemsQuery.refetch();
        onChanged?.();
      },
      onError: fail("Nie udało się usunąć wpisu"),
    });

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>Przebieg sesji</div>

        <div className={styles.modalBody}>
          <span className={styles.fieldLabel}>
            Z tych wpisów składany jest protokół. Zdarzenia obrad zapisują się same;
            dopisz wystąpienia, wnioski, interpelacje i zapytania.
          </span>

          {itemsQuery.isLoading ? (
            <Loading />
          ) : (
            <div className={styles.journalList}>
              {items.map((item) => (
                <div key={item.Id} className={styles.journalRow}>
                  <span className={styles.journalTime}>{formatTime(item.Date)}</span>

                  <div className={styles.conductItemBody}>
                    <span className={styles.journalSentence}>
                      {decodeText(item.Sentence) || decodeText(item.TypeLabel)}
                    </span>
                    {!!item.Description && (
                      <span className={styles.journalDescription}>
                        {decodeText(item.Description)}
                      </span>
                    )}
                  </div>

                  {item.IsManual && !readOnly && (
                    <div className={styles.rowTools}>
                      <button
                        type="button"
                        className={styles.toolBtn}
                        title="Edytuj wpis"
                        onClick={() => startEdit(item)}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        type="button"
                        className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                        title="Usuń wpis"
                        onClick={() => doRemove(item)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {!items.length && (
                <div className={styles.emptyAgenda}>
                  Przebieg sesji nie został jeszcze odnotowany.
                </div>
              )}
            </div>
          )}

          {!readOnly && !adding && (
            <button
              type="button"
              className={styles.addOptionButton}
              onClick={() => setAdding(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              Dodaj wpis
            </button>
          )}

          {!readOnly && adding && (
            <div className={styles.journalForm}>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Rodzaj wpisu</label>
                  <select
                    className={styles.textInput}
                    value={type}
                    onChange={(e) =>
                      setType(Number(e.target.value) as ECouncilProtocolItemType)
                    }
                  >
                    {MANUAL_PROTOCOL_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {protocolTypeLabel(t)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Punkt porządku obrad</label>
                  <select
                    className={styles.textInput}
                    value={agendaItemId}
                    onChange={(e) => setAgendaItemId(e.target.value)}
                  >
                    <option value="">poza punktami porządku</option>
                    {agendaItems.map((i) => (
                      <option key={i.Id} value={i.Id}>
                        {i.Order}. {decodeText(i.Title)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {needsPerson && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Osoba</label>
                  <select
                    className={styles.textInput}
                    value={personId}
                    onChange={(e) => setPersonId(e.target.value)}
                  >
                    <option value="">wskaż osobę</option>
                    <optgroup label="Radni">
                      {councilors.map((c) => (
                        <option key={c.Id} value={c.Id}>
                          {decodeText(c.LastName)} {decodeText(c.FirstName)}
                        </option>
                      ))}
                    </optgroup>
                    {!!guests.length && (
                      <optgroup label="Goście">
                        {guests.map((g) => (
                          <option key={g.Id} value={g.Id}>
                            {decodeText(g.LastName)} {decodeText(g.FirstName)}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}

              {needsDescription && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    {type === ECouncilProtocolItemType.PersonTookVote
                      ? "Treść wystąpienia"
                      : "Treść"}
                  </label>
                  <textarea
                    className={styles.textArea}
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="To trafi do protokołu w cudzysłowie"
                  />
                </div>
              )}

              <div className={styles.journalFormActions}>
                <HollowButton text="Anuluj" onClick={resetForm} />
                <Button
                  text={editing ? "Zapisz wpis" : "Dodaj wpis"}
                  loading={saving}
                  disabled={!canApply}
                  onClick={submit}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Zamknij" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default ProtocolJournalModal;
